import { Component, OnInit, ViewChild, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core'
import { ModalContainerComponent } from '../../_common/modal-container/modal-container.component'
import { CoreService } from '../../../services'
import { Product, OrderItem, Customer, Checkout, File, Currency, Category, Depot } from 'depoto-core/src/entities'
import { SchemaUtil } from '../../../utils'
import { DateUtil } from 'depoto-core/src/utils'

@Component({
  selector: 'modal-product-choose',
  templateUrl: 'modal-product-choose.component.html',
  styleUrls: ['modal-product-choose.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalProductChooseComponent implements OnInit {
  @ViewChild('modal') public childModal: ModalContainerComponent
  @Input() public checkout: Checkout
  @Input() public customer: Customer
  @Input() currency: Currency
  @Input() currencyDate: string
  @Input() dataCyAttribute: string
  @Input() orderCurrency: Currency
  @Input() public depots: Depot[] = []
  @Input() public orderItems: OrderItem[] = []
  @Output() public chosenProduct: EventEmitter<any> = new EventEmitter()
  @Output() public chosenOrderItem: EventEmitter<any> = new EventEmitter()
  searchValue = ''
  products: Product[] = []
  availabilityTypes: string[] = ['all', 'available', 'reservation', 'stock']
  selectedAvailability = 'available'
  selectedCategories: Category[] = []
  pageNumber = 1
  maxPageNumber = 999
  loading = false
  loadingNext = false
  hasSpecialAddOrderItemsRole = false
  intersectionObserver = null
  scrollDebounceTimeout = null

  constructor(protected core: CoreService) {}

  ngOnInit() {
    this.hasSpecialAddOrderItemsRole = this.core.services.user.hasRole('ROLE_ORDER_SELLITEMS')
  }

  get depotIds(): number[] {
    return this.depotScope ?? []
  }

  // Checkout and user depots are set, but none of them overlap, so no stock may be shown.
  get hasEmptyDepotScope(): boolean {
    return this.depotScope?.length === 0
  }

  // Null when stock visibility is unrestricted, otherwise the allowed depot ids (possibly empty).
  get depotScope(): number[] | null {
    const toIds = (depots: Depot[]): number[] => depots.map(depot => Number(depot.id)).filter(id => id > 0)
    const checkoutDepotIds = toIds(this.checkout?.depots ?? [])
    const userDepots = this.core.services.user.user?.depots ?? []
    if (!userDepots.length) {
      // No assigned depots means unrestricted stock visibility, so only the checkout scope applies.
      return checkoutDepotIds.length ? checkoutDepotIds : null
    }

    const userDepotIds = toIds(this.depots?.length ? this.depots : userDepots)
    if (!checkoutDepotIds.length) {
      return userDepotIds
    }
    // Stock is scoped to depots that are both on the order's checkout and visible to the user.
    return userDepotIds.filter(id => checkoutDepotIds.includes(id))
  }

  public toggleDepotsPanel(product: Product): void {
    product['isDepotsPanelOpen'] = !product['isDepotsPanelOpen']
  }

  // Adds only the single productDepot (batch) the user clicked, with the quantity from that
  // row's own input - not the whole picker's selection. Routes through the same chooseProduct
  // flow as the plain add button, just with a one-entry breakdown attached.
  public addSingleProductDepot(product: Product, selection: { productDepot: number; quantity: number }): void {
    product['selectedProductDepots'] = [selection]
    product.orderQuantity = selection.quantity
    this.chooseProduct(product)
  }

  // Already-committed productDepots breakdown for this product's order item, if any, so the
  // picker can show "already added: N" next to a batch instead of leaving that state invisible.
  public getExistingProductDepotsFor(product: Product): { productDepot: number; quantity: number }[] {
    const existing = this.orderItems?.find(item => item.product && item.product.id === product.id)
    return existing?.productDepots ?? []
  }

  public async chooseProduct(product: Product) {
    try {
      await this.chosenProduct.emit(product)
    } catch (err) {
      console.error(err)
    }
  }

  async searchForProduct(addPage?: number) {
    if (addPage) {
      this.pageNumber += addPage
      this.loadingNext = true
    } else {
      this.pageNumber = 1
    }
    this.loading = true
    try {
      const result = await this.core.services.product.getList(
        {
          page: this.pageNumber,
          currency: this.currency?.id ? this.currency?.id : 'CZK',
          currencyDate: DateUtil.getDateStringFromUTCString(this.currencyDate),
          filters: {
            fulltext: this.searchValue.trim(),
            listType: 'children_and_parents_without_children',
            availability: this.selectedAvailability,
            categories: this.selectedCategories?.length > 0 ? this.selectedCategories?.map(cat => cat.id) : [],
            enabled: true,
          },
        },
        SchemaUtil.productOrderAdd
      )
      this.maxPageNumber = result.paginator.endPage
      const products = result.items.map(p => new Product(p))
      this.recalculatePricesByCurrency(products)
      this.products = this.pageNumber === 1 ? products : [...this.products, ...products]
      setTimeout(() => this.initIntersectionObserver(), 300)
    } finally {
      // always release the loading state, even when the request fails,
      // so the "add product" button and modal don't get stuck spinning forever
      this.loading = false
      this.loadingNext = true
    }
  }

  recalculatePricesByCurrency(products) {
    // todo data on products are changed, after we are going to use more cached data, we cannot change data on products
    for (const product of products) {
      if (product.purchaseCurrency?.id !== this.currency?.id) {
        product.actualSellPrice = this.recalculatePrice(
          product.actualSellPrice,
          this.orderCurrency,
          this.core.services.user.user.company.currency
        )
      }
    }
  }

  recalculatePrice(price, newCurrency, originalCurrency?) {
    let recalculatedPrice: number
    if (originalCurrency.id === 'CZK') {
      recalculatedPrice = Number((price * newCurrency.ratio).toFixed(2))
    } else if (newCurrency.id === 'CZK') {
      recalculatedPrice = Number((price / originalCurrency.ratio).toFixed(2))
    } else if (originalCurrency.id !== 'CZK' && newCurrency.id !== 'CZK') {
      const priceInCzk = Number(price / newCurrency.ratio)
      recalculatedPrice = Number((priceInCzk * newCurrency.ratio).toFixed(2))
    }
    return recalculatedPrice
  }

  public async showChildModal() {
    this.loading = true
    try {
      await this.searchForProduct()
      this.childModal.showChildModal()
    } catch (err) {
      console.error(err)
      this.loading = false
      this.loadingNext = false
    }
  }

  public chosenProductDepot(orderItem: OrderItem) {
    this.chosenOrderItem.emit(orderItem)
  }

  getThumbUrl(image: File): string {
    return image.thumbnails?.length > 0 ? image.thumbnails.filter(t => t.format === 'w135wp')[0].url : ''
  }

  updateQuantityFromPacks(product, pack, quantity) {
    pack['quantityTmp'] = Number(quantity)
    product.orderQuantity = product.packs
      .map(p => (p['quantityTmp'] || 0) * p.quantity)
      .reduce((a, b) => Number(a) + Number(b))
  }

  initIntersectionObserver() {
    const el = document.getElementById('next-page-btn')
    if (el) {
      if (this.intersectionObserver) {
        this.intersectionObserver.unobserve(el)
      }
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !this.loading && !this.scrollDebounceTimeout) {
            this.searchForProduct(1)
            this.scrollDebounceTimeout = setTimeout(() => {
              this.scrollDebounceTimeout = null
            }, 700)
          }
        },
        { threshold: [0] }
      )
      this.intersectionObserver.observe(el)
    }
  }
}
